/* eslint-disable react-hooks/exhaustive-deps */
import * as THREE from 'three';
import React, { useMemo } from 'react';
import { useThree } from '@react-three/fiber';

import { const_var, params } from '../../../../../../../redux/reducers/reducer';

import { delModel, extrudeSettings, textureAnisotropy } from '../../../../../../../utils/Functions';
import { CSG } from '../../../../../../../assets/libs/csg-lib';
import { MaterialInnerWall } from '../../../../../CustomMaterial';

const InsideWallModel = () => {
    const { gl } = useThree();
    const texture = const_var.metalTexture
    
    const wallModel = useMemo(() => {
        let model = new THREE.Shape();

        const leftHeight = (params.singleSlope && params.freeStandingSide === "left") ? params.p_h - ((params.p_r_p / 12) * params.p_w) : params.p_h
        const rightHeight = (params.singleSlope && params.freeStandingSide === "right") ? params.p_h + ((params.p_r_p / 12) * params.p_w) : params.p_h

        if (params.p_r_s === 1) {
            const innerRoofWidth = params.p_w - 0.1
            const innerRoofPorchSize = params.p_r_p - 0.02
            
            model.moveTo(-innerRoofWidth / 2 + 0.0001, params.p_h);

            model.lineTo(-innerRoofWidth / 2, params.p_h + 0.3);
            model.lineTo(-innerRoofWidth / 2 + 0.02, params.p_h + 0.34);
            model.lineTo(-innerRoofWidth / 2 + 0.055, params.p_h + 0.38);
            model.lineTo(-innerRoofWidth / 2 + 0.1, params.p_h + 0.4);
    
            model.lineTo(0, params.p_h + (innerRoofPorchSize / 12) * innerRoofWidth / 2 + 0.4);
    
            model.lineTo(innerRoofWidth / 2 - 0.1, params.p_h + 0.4);
            model.lineTo(innerRoofWidth / 2 - 0.055, params.p_h + 0.38);
            model.lineTo(innerRoofWidth / 2 - 0.02, params.p_h + 0.34);
            model.lineTo(innerRoofWidth / 2, params.p_h + 0.3);
    
            model.lineTo(innerRoofWidth / 2 +0.005, params.p_h);

            model.lineTo(innerRoofWidth / 2 +0.005, 0.01);
            model.lineTo(-innerRoofWidth / 2 +0.005, 0.01);
    
            model.closePath()
    
        } else if (params.p_r_s === 2 || params.p_r_s === 3) {
            model.moveTo(-(params.p_w / 2 - 0.05), leftHeight - 0.001);
            if(!params.singleSlope) model.lineTo(0, params.p_h + (params.p_r_p / 12) * params.p_w / 2);
            model.lineTo((params.p_w / 2 - 0.05), rightHeight - 0.01);
            model.lineTo((params.p_w / 2 - 0.05), 0.01);
            model.lineTo(-(params.p_w / 2 - 0.05), 0.01);
            model.closePath()
        }

        return model;
    
    }, [params.p_w, params.p_h, params.p_r_s, params.p_r_p, params.singleSlope && params.freeStandingSide === "left"])

    
    /************** cutting hole using CSG ***************/
    const CSGWallModelGeometry = useMemo(() => {
        const meshWallModel = new THREE.Mesh(new THREE.ExtrudeGeometry(wallModel, extrudeSettings(0.005)))
        const meshDelModel = new THREE.Mesh(new THREE.ExtrudeGeometry(delModel('backStorageBack', 'wall'), extrudeSettings(1)))
        const meshRightOffsetWithDoorDelModel = new THREE.Mesh(new THREE.ExtrudeGeometry(delModel('right', 'wall'), extrudeSettings(1)))
        const meshLeftOffsetWithDoorDelModel = new THREE.Mesh(new THREE.ExtrudeGeometry(delModel('left', 'wall'), extrudeSettings(1)))

        let meshWallModel_Temp = meshWallModel.clone();
        meshWallModel_Temp.position.set(0, 0, -params.p_d / 2 - 0.13)
        meshWallModel_Temp.updateMatrixWorld();

        let meshDelModel_Temp = meshDelModel.clone();
        meshDelModel_Temp.position.set(0, 0, -params.p_d / 2 - 0.13 - 0.5)
        meshDelModel_Temp.updateMatrixWorld();

        let meshRightOffsetWithDoorDelModel_Temp = meshRightOffsetWithDoorDelModel.clone();
        meshRightOffsetWithDoorDelModel_Temp.position.set(params.p_w / 2 + 0.75, 0, -params.endStoragePositionZ);
        meshRightOffsetWithDoorDelModel_Temp.rotateY(-Math.PI / 2)
        meshRightOffsetWithDoorDelModel_Temp.updateMatrixWorld();

        let meshLeftOffsetWithDoorDelModel_Temp = meshLeftOffsetWithDoorDelModel.clone();
        meshLeftOffsetWithDoorDelModel_Temp.position.set(-params.p_w / 2 + 0.25, 0, -params.endStoragePositionZ);
        meshLeftOffsetWithDoorDelModel_Temp.rotateY(-Math.PI / 2)
        meshLeftOffsetWithDoorDelModel_Temp.updateMatrixWorld();

        // mesh to cut the dutch model
        const meshWallModel_CSG = CSG.fromMesh(meshWallModel_Temp);
        const meshDelModel_CSG = CSG.fromMesh(meshDelModel_Temp);
        const meshRightOffsetWithDoorDelModel_CSG = CSG.fromMesh(meshRightOffsetWithDoorDelModel_Temp);
        const meshLeftOffsetWithDoorDelModel_CSG = CSG.fromMesh(meshLeftOffsetWithDoorDelModel_Temp);

        const wallModel_CSG = meshWallModel_CSG.subtract(meshDelModel_CSG);
        const rightWallDel_CSG = wallModel_CSG.subtract(meshRightOffsetWithDoorDelModel_CSG);
        const leftWallDel_CSG = rightWallDel_CSG.subtract(meshLeftOffsetWithDoorDelModel_CSG);

        const CSGWallModel = CSG.toMesh(
            leftWallDel_CSG,
            new THREE.Matrix4(),
        )

        return CSGWallModel.geometry;

    }, [params.p_d, params.p_w, params.p_h, params.p_b_w, params.endStoragePositionZ, params.p_r_s, params.p_r_p, params.singleSlope && params.freeStandingSide === "left", const_var.newDoorsArray]);

    useMemo(() => {
        textureAnisotropy(gl, texture, 1.3, 1.3, params.p_v_w ? Math.PI / 2 : 0);
    }, [params.p_v_w])

    return CSGWallModelGeometry && (
        <mesh name="c_b_b_s_w_back-inside-wall" geometry={CSGWallModelGeometry} position={[0, 0, -0.02]}>
            <MaterialInnerWall texture={texture} visibleSection={"center"} />
            {/* <meshStandardMaterial color='green' /> */}
        </mesh>
    )
}

export default InsideWallModel